import * as undoActions from 'owa-mail-undo';
import DisposalType from 'owa-service/lib/contract/DisposalType';
import FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import getDeleteAllConfirmText from './helpers/getDeleteAllConfirmText';
import getSelectedFolder from 'owa-mail-store/lib/utils/getSelectedFolder';
import { isUnderDeletedItemsFolderFromAnyMailbox } from 'owa-mail-store';
import strings from 'owa-strings/owa-mail-strings';
import { action } from 'satcheljs/lib/legacy';
import { ActionSource } from 'owa-mail-store';
import { confirm, ConfirmCustomizationOptions, DialogResponse } from 'owa-confirm-dialog';
import ViewFilter from 'owa-service/lib/contract/ViewFilter';
import folderStore, { Folder } from 'owa-folders';
import { format } from 'owa-strings';
import { getListViewTypeForFolder, listViewStore } from 'owa-mail-list-store';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { lazyOnBulkActionSubmitted } from 'owa-bulk-action-store';
import { logUsage } from 'owa-analytics';
import getEmptyFolderHelperProperties from './helpers/emptyFolderHelper';
import getFolderScopeText from './helpers/getFolderScopeText';
import emptyFolderWithoutConfirmation from './actionCreators/emptyFolderWithoutConfirmation';

/**
 * Get the type of items to delete based on view filter
 * @param viewFilter the view filter
 * @returns the text which indicates type of items to delete based on the view filter
 */
function getItemsTypeText(viewFilter: ViewFilter): string {
    switch (viewFilter) {
        case 'Unread':
            return strings.allUnreadItems;

        case 'Flagged':
            return strings.allFlaggedItems;

        case 'ToOrCcMe':
            return strings.allToMeItems;

        case 'All':
        default:
            return strings.allItems;
    }
}

const emptyFolderActionName = 'emptyFolderAction';
export { emptyFolderActionName };

/**
 * Delete all conversations or items in the specified folder
 * @param folder the folder to be emptied
 * @param focusedViewFilter the focused view filter
 * @param viewFilter the view filter
 * @param isExplicitSoftDelete flag to indicate whether disposal type is soft delete
 * @param clientLastSyncTime client last sync time used as a watermark
 * @param rowKeysToExclude row keyss to exclude from empty folder operation
 */
export default action(emptyFolderActionName)(function emptyFolder(
    folder: Folder,
    focusedViewFilter: FocusedViewFilter,
    viewFilter: ViewFilter,
    isExplicitSoftDelete: boolean,
    actionSource: ActionSource,
    rowKeysToExclude: string[],
    clientLastSyncTime: Date,
    tableViewId?: string
): Promise<void> {
    let confirmEmptyFolderSubtext;
    let disposalType: DisposalType;
    let deleteSubFolders;

    undoActions.clearLastUndoableAction();

    const folderScopeText = getFolderScopeText(focusedViewFilter, folder.DisplayName);
    const itemsTypeText = getItemsTypeText(viewFilter);
    const exclusionListCount = rowKeysToExclude ? rowKeysToExclude.length : 0;
    let confirmEmptyFolderSubTextString;
    let folderToBeEmptied = folder;

    // If a tableViewId is specified we are emptying the table, in which case we want get the actual folder
    // to get the emptyFolder helper properties
    if (tableViewId) {
        const tableView = listViewStore.tableViews.get(tableViewId);
        folderToBeEmptied = folderStore.folderTable.get(tableView.tableQuery.folderId);
    }

    const {
        isFolderDeletedItemsFolder,
        isFolderDumpsterFolder,
        deletedItemsFolderId,
        shouldSupportBulkActions,
        treeType,
    } = getEmptyFolderHelperProperties(folderToBeEmptied);

    if (isFolderDumpsterFolder) {
        confirmEmptyFolderSubTextString = strings.confirmPermanentEmptyFolderSubText;
        deleteSubFolders = false;
        disposalType = 'HardDelete';
    } else if (isFolderDeletedItemsFolder || folder.DistinguishedFolderId == 'junkemail') {
        confirmEmptyFolderSubTextString = strings.confirmPermanentEmptyFolderSubText;
        deleteSubFolders = !isConsumer();
        // This is to ensure the contract on server that clientLastSyncTime and deleteSubFolders cannot be present at the same time
        clientLastSyncTime = deleteSubFolders ? null : clientLastSyncTime;
        disposalType = 'SoftDelete';
    } else if (isUnderDeletedItemsFolderFromAnyMailbox(folder)) {
        confirmEmptyFolderSubTextString =
            strings.confirmEmptyFolderForFolderUnderDeletedItemsSubText;
        deleteSubFolders = false;
        disposalType = 'SoftDelete';
    } else {
        if (isExplicitSoftDelete) {
            confirmEmptyFolderSubTextString = strings.confirmPermanentEmptyFolderSubText;
            disposalType = 'SoftDelete';
        } else {
            confirmEmptyFolderSubTextString = strings.confirmEmptyFolderSubText;
            disposalType = 'MoveToDeletedItems';
        }

        deleteSubFolders = false;
    }

    confirmEmptyFolderSubtext = format(
        confirmEmptyFolderSubTextString,
        itemsTypeText,
        folderScopeText
    );

    return confirm(
        getDeleteAllConfirmText(
            actionSource,
            getListViewTypeForFolder(folder.FolderId.Id) /* listViewType */,
            folder.TotalCount - exclusionListCount
        ),
        confirmEmptyFolderSubtext,
        false /* resolveImmediately */,
        <ConfirmCustomizationOptions>{
            // Wait until dialog is dismissed to avoid focus interruption
            delayCallbackAfterAnimation: true,
            okText: strings.confirmEmptyFolderText,
        }
    ).then(async (response: DialogResponse) => {
        if (response === DialogResponse.ok) {
            if (shouldSupportBulkActions) {
                lazyOnBulkActionSubmitted.importAndExecute(
                    folder.FolderId.Id,
                    'EmptyFolder' /* scenario */,
                    null /* customData */,
                    folder.TotalCount - exclusionListCount /* totalCount */
                );
            }

            // TODO : Add check for shared folders during implementation
            logUsage('TnS_EmptyFolder', [
                actionSource,
                folder.TotalCount,
                folderIdToName(folder.FolderId.Id),
                folderIdToName(getSelectedFolder().FolderId.Id),
                disposalType,
                treeType,
            ]);

            emptyFolderWithoutConfirmation(
                folder,
                disposalType,
                deleteSubFolders,
                clientLastSyncTime,
                rowKeysToExclude,
                tableViewId,
                deletedItemsFolderId,
                shouldSupportBulkActions
            );
        }
    });
});
